/**
 * File serves the Tag Management Page helps to manage the tags and perform CRUD operations to the tags
 */

import React, { useEffect, useState } from 'react'
import TagsModal from './TagsModal';
import { MdAdd, MdDelete, MdRestore } from 'react-icons/md';
import Service from '../../Service/http';
import { useMutation, useQuery } from 'react-query';
import { Pagination, Switch, Tooltip, Typography } from '@mui/material';
import { FaDownload, FaEdit, FaUpload } from 'react-icons/fa';
import { handleFileUpload } from '../../Utils/UtilityFunctions';
import { Link, useSearchParams } from 'react-router-dom';
import SkeletonRows from '../../SkeletonRows/SkeletonRows';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';

const Tags = () => {

    // * --------------------------------  States and constants  ------------------------------------*
    
    const service = new Service();

    const SHOW_ALL_TAGS = 'show-all-tags'
    const SHOW_DELETED = 'show-deleted'

    // States --------

    const [tags, setTags] = useState([])
    const [tagsFile, setTagsFile] = useState([])
    const [searchParams, setSearchParams] = useSearchParams();
    
    // string states
    const [tagName, setTagName] = useState('')
    const [tagDescription, setTagDescription] = useState('')
    const [modalAction, setModalAction] = useState('add') // add or update
    const [tagId, setTagId] = useState('')
    
    // boolean states
    const [showModal, setShowModal] = useState(false);
    const [showDeleted, setShowDeleted] = useState(JSON.parse(localStorage.getItem(SHOW_DELETED)) || false);
    const [showallTags, setShowAllTags] = useState(JSON.parse(localStorage.getItem(SHOW_ALL_TAGS)) || false);
    
    const [page, setPage] = useState(searchParams.get('page') || 1)
    const [limit, setLimit] = useState(searchParams.get('limit') || 10)
    
    // Constants --------

    const tableHeaders = ['Tag Label', 'Tag', 'Created At', !showDeleted ? 'Created By' : 'Deleted By' , 'Edit', !showDeleted ? 'Delete' : 'Restore']
    const limits = [10, 20, 50, 100]

    // * --------------------    Functions and Methods  ------------------------ *

    const generateFile = (e) => {
        handleFileUpload(e, setTagsFile);
    }
    
    const fetchAllTags = async () => {

        const response = await service.get(`api/tags/all?page=${page}&limit=${limit}&isDeleted=${showDeleted}`);
        return response.data;

    }

    const fetchTagsWithoutFilter = async () => {

        const response = await service.get('api/tags');
        return response.data;

    }

    // * ----------------------- Use Query Functions -------------------------- *

    const { data, isLoading : loadingTags, isFetching: fetchingTags , refetch } = useQuery(["allTags", page, limit, showDeleted], () => fetchAllTags() , {
        onSuccess(json) {
            setTags(json.tags);
            setPage(Math.min(page, Number(json.totalPages)))
        },
        refetchOnWindowFocus : false
    });

    const { data : allTagsWithoutFilter, refetch: refetchAll } = useQuery(['allTagsWithoutFilter', showallTags], fetchTagsWithoutFilter, {
        onError(error) {
            console.error(error);   
        },
        refetchOnWindowFocus: false
    })
    

    const handleTagEdit = ( tagId, tagName, tagDescription ) => {
        setTagName(tagName)
        setTagDescription(tagDescription);
        setModalAction('update')
        setShowModal(true);
        setTagId(tagId);
    }

    const handleTagDeleteOrRestore = ( id, action ) => {
        setTagId(id);
        setModalAction(action);
        setShowModal(true);
    }

    const handleShowDeleted = () => {
        localStorage.setItem(SHOW_DELETED, !showDeleted);
        setShowDeleted(!showDeleted)
    }

    const handleShowAllTags = () => {
        localStorage.setItem(SHOW_ALL_TAGS, !showallTags)
        setShowAllTags(!showallTags)
    }

    const { mutate, isLoading: uploadingTags } = useMutation(['tagsUpload'], () => uploadMultipleTags(), {
        onError(error) {
            console.error(error);
            toast.error("Can't Upload Tags!");
        },
        onSuccess(json) {
            toast.success('Uploaded Tags Successfully!')
            refetchAll();
        }
    })

    const uploadMultipleTags = async () => {
        const response = await service.post('api/tags/add/multiple', { tags : tagsFile });
        return response.data;
    }
    // <---------------------------  UI Components ------------------------------->

    const tableRows = tags.map((tag, index) => {

        return <tr key={index}>
            <td className='py-2'>
                <strong>{tag?.label}</strong>
            </td>
            <td className='py-2'>
                <Tooltip title={<Typography>{tag?.description || tag?.value}</Typography>} placement='right' arrow>
                     <span className='badge bg-primary' style={{cursor: 'pointer'}}>{tag.value}</span>
                </Tooltip>
            </td>
            <td className='py-2'>{new Date(tag?.createdAt).toDateString()}</td>
            <td className='py-2'>
                <Tooltip title={tag.createdBy.email} placement='top' arrow>
                    <span>{showDeleted ? tag?.deletedBy?.name : tag?.createdBy?.name}</span>
                </Tooltip>
            </td>
            <td className='py-1'>
                <div className='w-100 d-flex justify-content-center align-items-center'>
                    <button 
                        className={"custom-button bg-transparent"}
                        onClick={() => handleTagEdit( tag._id, tag.label, tag.description )}
                        disabled={tag.isDeleted}
                    >
                        <FaEdit color={tag.isDeleted ? '#cdcdcd' : 'black'}/>
                    </button>
                </div>
            </td>
            <td className='py-0'>
                <div className='w-100 d-flex justify-content-center align-items-center'>
                    <button 
                        className="custom-button bg-transparent"
                        onClick={() => handleTagDeleteOrRestore(tag._id, tag.isDeleted ? 'restore' : 'delete')}
                    >
                        {tag.isDeleted ? <MdRestore color='green' size={22}/> : <MdDelete color='red' size={22}/>}
                    </button>
                </div>
            </td>
        </tr>
    });

    const switchStyles = {

        width: 40,
        height: 26,
        padding: 0,
        margin: 1,
        '& .MuiSwitch-switchBase': {
            padding: 0,
            transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(19px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
            backgroundColor: showallTags ? 'primary' : 'error.light',
            opacity: 1,
            border: 0,
            },
        },
        },
        '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 22,
            height: 22,
            margin: '2px 0px'
        },
        '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: '#E9E9EA',
            opacity: 1,
        },

    }

    useEffect(() => {
        if(tagsFile.length > 0) mutate()
    }, [tagsFile, mutate])

    useEffect(() => {
        setSearchParams({
            page,
            limit,
            isDeleted : showDeleted
        })
    }, [setSearchParams, page, limit, showDeleted])

    // * ------------------------- Views ------------------------------- *

    const view1 = (
        
        <div className='overflow-auto'>
            <table className='table table-bordered mt-3'>
                    <thead>
                    <tr>
                        { tableHeaders.map((head, index) => <th className='text-center' key={index}>{ head }</th>)}
                    </tr>
                    </thead>
                    <tbody>
                        {
                            !(loadingTags || fetchingTags) ? tableRows : <SkeletonRows rows={10} cols={6} />
                        }
                    </tbody>
            </table>

            <div className='w-100 d-flex justify-content-center'>
                <Pagination
                    page={Number(page)}
                    onChange={(e, v) => setPage(v)}
                    variant="outlined"
                    shape="rounded"
                    count={data?.totalPages}
                    color='primary'
                />
                <select 
                    onChange={(e) => setLimit(e.target.value)} 
                    value={limit} 
                    className='mx-3'
                    style={{width: '4rem', height: '2rem', borderRadius: '5px', border: '1px solid gray', outline: 'none', textAlign: 'center', margin: '0px 20px'}}
                >
                    {
                        limits.map((limit, index) => {
                            return <option value={limit} key={index}>{limit}</option>
                        })
                    }
                </select>
            </div>
        </div>
        
    )

    const view2 = (
        <div className='container border rounded overflow-auto align-items-center d-flex flex-wrap bg-white p-2' style={{maxHeight: '80vh'}}>
            {
                allTagsWithoutFilter?.map((tag, index) => {
                    return (
                        <Tooltip title={tag.description ?? tag.label} arrow key={index}>
                            <span 
                                className={`badge bg-${tag.isDeleted ? 'danger' : 'primary'} p-2 m-1`}
                                style={{cursor: 'pointer'}}
                                onClick={() => handleTagEdit(tag._id, tag.label, tag.description)}
                            >
                            {tag.value}
                            </span>
                        </Tooltip>
                    )
                })
            }
        </div>
    )
    // * ------------------------ UI return statement ------------------------------ *

    return (
        <>
            <div className='container'>
                {/* Modal */}
                <TagsModal 
                    show={showModal} 
                    onHide={() => setShowModal(false)} 
                    tagName={tagName} 
                    setTagName={setTagName}
                    tagDescription={tagDescription}
                    setTagDescription={setTagDescription}
                    refetch={refetch}
                    refetchAll={refetchAll}
                    action={modalAction}
                    tagId={tagId}
                    setShowDeleted={setShowDeleted}
                />

                {/* Header */}
                <div className='create-tag d-flex w-100 justify-content-between align-items-center my-3 border rounded p-2'>
                    <div className='w-100'>
                        <Switch 
                            onChange={handleShowAllTags} 
                            checked={showallTags}
                            sx={switchStyles}
                        />
                        { !showallTags ? <span>Show All Tags</span> : <span>Hide All Tags</span>}
                        {
                            !showallTags && <Switch 
                                onChange={handleShowDeleted} 
                                checked={showDeleted}
                                sx={switchStyles}
                            />
                        }
                        { !showallTags ? !showDeleted ? <span>Show Deleted</span> : <span className='text-danger'>Deleted</span> : null}
                    </div>
                    <div className='w-100 d-flex justify-content-end'>
                        <button className="custom-button text-light bg-success" 
                            style={{fontWeight: 'bold', height: '35px'}}
                            onClick={() => {
                                setShowModal(true)
                                setModalAction('add')
                            }}
                        >
                            New Tag <MdAdd/>
                        </button>
                            <Tooltip title={<Typography>Upload Multiple Tags</Typography>} arrow>
                                <label htmlFor="upload-multiple-tags" className='custom-button border mx-2 text-primary d-flex align-items-center'>
                                    {!uploadingTags ? <FaUpload size={20} style={{cursor: 'pointer'}}/> : <ClipLoader size={22} color='#3498db'/>}
                                </label>
                            </Tooltip>
                            <Tooltip title={<Typography>Download Sample</Typography>} arrow>
                                <Link
                                    className="custom-button border text-primary"
                                    to="/files/SampleMultipleTagsUpload.xlsx"
                                    target="_blank"
                                    download
                                    >
                                    <FaDownload size={20} />
                                </Link>
                            </Tooltip>
                        <input type="file" id='upload-multiple-tags' onChange={(e) => generateFile(e)} className='d-none'/>
                    </div>
                </div>

                {/* Tags Table Or All Tags*/}

                { !showallTags ? view1 : view2 }
                        
            </div>
        </>
    )
}

export default Tags